/**
 * Historical columns for the Projections "Historical" view.
 *
 * The view shows one YTD column per display year (getDisplayHistoricalYears):
 * 3 normally, 4 during the Oct 1 - Dec 31 window, when the year before the
 * target is still partial (e.g. 2026 with 9 closed months while GMs enter 2027).
 *
 * "Annualize" is applied at the data layer: the partial year's bundle is
 * rebuilt so every YTD field = (sum of its CLOSED months) x 12 / monthsClosed.
 * The renderers only read YTD fields, so every row (gross, expenses, units,
 * accounts) annualizes consistently and ratio rows (GP%, % of gross, per-unit)
 * stay unchanged.
 */

import { MonthlyTotals } from '../types';
import {
  AccountData,
  DOCGrossData,
  HistoricalYearBundle,
} from '../components/projections/projectionInputTypes';
import { UnitCountsData } from '../services/api';
import { annualizeClosedMonths } from '../types/projections';

export interface YearDataResult {
  accountDataMap: Map<string, AccountData>;
  docGrossMap: Map<string, DOCGrossData>;
  unitCountsData: UnitCountsData | null;
}

export interface HistoricalColumn {
  year: number;
  /** Header text, e.g. "2025 YTD", "2026 YTD (9 mo)", "2026 Annualized" */
  label: string;
  data: YearDataResult;
  /** Location total gross for this column (for "% of location gross" cells) */
  locationTotalGross: number;
  /** True for the newest column (rendered bold) */
  isNewest: boolean;
}

/** Non-hook equivalent of useYearData, so a variable number of years can be built. */
export function buildYearData(bundle: HistoricalYearBundle | null): YearDataResult {
  const accountDataMap = new Map<string, AccountData>();
  const docGrossMap = new Map<string, DOCGrossData>();
  if (bundle) {
    bundle.accounts.forEach(acc => accountDataMap.set(acc.account, acc));
    bundle.docGross.forEach(dg => docGrossMap.set(dg.department, dg));
  }
  return { accountDataMap, docGrossMap, unitCountsData: bundle?.unitCountsData || null };
}

type Monthly = object | null | undefined;

const annualizeMap = (
  map: Record<string, number> | undefined,
  monthlyByKey: Record<string, MonthlyTotals> | undefined,
  monthsClosed: number,
  rawTotal: number,
  annualizedTotal: number
) => {
  if (!map) return map;
  const out: Record<string, number> = {};
  for (const key of Object.keys(map)) {
    const monthly = monthlyByKey?.[key];
    // Without a per-key monthly breakdown the closed months can't be isolated, so scale
    // by the total's closed-month ratio (keeps brands consistent with the total and
    // excludes the in-progress month the same way)
    out[key] = monthly
      ? annualizeClosedMonths(monthly, monthsClosed)
      : rawTotal > 0 ? Math.round(map[key] * annualizedTotal / rawTotal) : 0;
  }
  return out;
};

/**
 * Rebuild a partial year's bundle with annualized YTD fields
 * (closed months only x 12 / monthsClosed). Complete years pass through.
 */
export function annualizeBundle(
  bundle: HistoricalYearBundle | null,
  monthsClosed: number
): HistoricalYearBundle | null {
  if (!bundle || monthsClosed <= 0 || monthsClosed >= 12) return bundle;
  const a = (monthly: Monthly, fallback: number | undefined) =>
    monthly ? annualizeClosedMonths(monthly, monthsClosed) : fallback;

  const units = bundle.unitCountsData;
  const newTotal = units ? a(units.monthlyNewUnits, units.totalNewUnits) ?? 0 : 0;
  const usedTotal = units ? a(units.monthlyUsedUnits, units.totalUsedUnits) ?? 0 : 0;
  return {
    ...bundle,
    accounts: bundle.accounts.map(acc => ({
      ...acc,
      ytdTotal: annualizeClosedMonths(acc.monthlyTotals, monthsClosed),
    })),
    docGross: bundle.docGross.map(dg => ({
      ...dg,
      ytdGross: a((dg.gross || dg.monthlyGross), dg.ytdGross) ?? 0,
      ytdSales: a(dg.sales, dg.ytdSales),
      ytdCos: a(dg.cos, dg.ytdCos),
      ytdTotalGross: a(dg.totalGross, dg.ytdTotalGross),
      ytdTotalExpenses: a(dg.totalExpenses, dg.ytdTotalExpenses),
      ytdOperatingProfit: a(dg.operatingProfit, dg.ytdOperatingProfit),
    })),
    unitCountsData: units ? {
      ...units,
      totalNewUnits: newTotal,
      totalUsedUnits: usedTotal,
      newVehicleUnits: annualizeMap(units.newVehicleUnits, units.monthlyNewUnitsByBrand, monthsClosed, units.totalNewUnits, newTotal) || {},
      usedVehicleUnits: annualizeMap(units.usedVehicleUnits, units.monthlyUsedUnitsByBrand, monthsClosed, units.totalUsedUnits, usedTotal) || {},
    } : null,
  };
}

/** Header label for a display-year column. */
export function historicalColumnLabel(year: number, monthsClosed: number, annualized: boolean): string {
  if (monthsClosed > 0 && monthsClosed < 12) {
    return annualized ? `${year} Annualized` : `${year} YTD (${monthsClosed} mo)`;
  }
  return `${year} YTD`;
}
